import * as Yup from "yup";
import { FormField } from "@/components/common/form-field";
import { FIELD_LABEL } from "@/features/clients/components/profile-form-shared";
import { CheckboxField } from "@/features/clients/components/record-form-fields";
import { accountsApi } from "@/features/worksheets/api";
import { CrudDialog } from "@/features/worksheets/components/crud-dialog";
import { MoneyField } from "@/features/worksheets/components/money-field";
import { centsToDollars, dollarsToCents } from "@/features/worksheets/money";
import { ACCOUNT_CLASS_OPTIONS, type AccountClass, type FinancialAccount } from "@/features/worksheets/types";

interface AccountFormValues {
  institution: string;
  accountType: string;
  // Sensitive: plain writable account number. Never console.log this value.
  accountNumber: string;
  accountValue: string;
  loanBalance: string;
  creditLimit: string;
  qsRate: string;
  keyAccess: boolean;
}

const validationSchema = Yup.object({
  institution: Yup.string().required("Institution is required"),
  qsRate: Yup.string()
    .matches(/^\d*(\.\d+)?$/, "Enter a percent")
    .test("range", "Must be between 0 and 100", (v) => !v || (Number(v) >= 0 && Number(v) <= 100)),
});

function toInitialValues(record: FinancialAccount | null): AccountFormValues {
  return {
    institution: record?.institution ?? "",
    accountType: record?.accountType ?? "",
    // Read shape exposes only the masked label; the writable number starts blank
    // on edit so we never round-trip (or expose) the raw value.
    accountNumber: "",
    accountValue: record ? centsToDollars(record.accountValue) : "",
    loanBalance: record ? centsToDollars(record.loanBalance) : "",
    creditLimit: record ? centsToDollars(record.creditLimit) : "",
    qsRate: record?.qsRate ?? "",
    keyAccess: record?.keyAccess ?? false,
  };
}

function toPayload(v: AccountFormValues, accountClass: AccountClass): Record<string, unknown> {
  const payload: Record<string, unknown> = {
    accountClass,
    institution: v.institution,
    accountType: v.accountType,
    accountValue: dollarsToCents(v.accountValue),
    loanBalance: dollarsToCents(v.loanBalance),
  };
  // Only send the account number when the user actually entered one — an empty
  // field on edit means "leave the stored number unchanged". Never send the
  // read-only masked `accountNumberLabel`.
  if (v.accountNumber.trim() !== "") payload.accountNumber = v.accountNumber.trim();
  if (accountClass === "C") payload.creditLimit = dollarsToCents(v.creditLimit);
  if (accountClass === "R") payload.qsRate = v.qsRate;
  if (accountClass === "V") payload.keyAccess = v.keyAccess;
  return payload;
}

const CLASS_LABEL = (accountClass: AccountClass) =>
  ACCOUNT_CLASS_OPTIONS.find((o) => o.value === accountClass)?.label ?? "";

export function AccountDialog({
  caseId,
  accountClass,
  record,
  open,
  onOpenChange,
  onSaved,
}: {
  caseId: string;
  accountClass: AccountClass;
  record: FinancialAccount | null;
  open: boolean;
  onOpenChange: (o: boolean) => void;
  onSaved: () => void;
}) {
  async function onSave(values: AccountFormValues) {
    const payload = toPayload(values, accountClass);
    if (record) {
      await accountsApi.update(caseId, record.id, payload);
    } else {
      await accountsApi.create(caseId, payload);
    }
  }

  return (
    <CrudDialog
      open={open}
      onOpenChange={onOpenChange}
      editing={record !== null}
      noun="Account"
      initialValues={toInitialValues(record)}
      validationSchema={validationSchema}
      onSave={onSave}
      onSaved={onSaved}
    >
      <div className="grid gap-2">
        <span className={FIELD_LABEL}>Account Type</span>
        <p className="text-sm">{CLASS_LABEL(accountClass)}</p>
      </div>
      <div className="grid grid-cols-2 gap-4">
        <FormField
          name="institution"
          label="Institution"
          labelClassName={FIELD_LABEL}
          placeholder="e.g. First National Bank"
        />
        <FormField
          name="accountType"
          label="Type"
          labelClassName={FIELD_LABEL}
          placeholder="e.g. Checking"
        />
      </div>
      <FormField
        name="accountNumber"
        label="Account Number"
        labelClassName={FIELD_LABEL}
        autoComplete="off"
        placeholder={record ? `On file: ${record.accountNumberLabel}` : "Account number"}
      />
      <div className="grid grid-cols-2 gap-4">
        <MoneyField name="accountValue" label="Value" />
        <MoneyField name="loanBalance" label="Loan Balance" />
      </div>
      {accountClass === "C" && (
        <div className="grid grid-cols-2 gap-4">
          <MoneyField name="creditLimit" label="Credit Limit" />
        </div>
      )}
      {accountClass === "R" && (
        <FormField
          name="qsRate"
          label="Quick-Sale Rate (%)"
          labelClassName={FIELD_LABEL}
          inputMode="decimal"
          placeholder="e.g. 80"
        />
      )}
      {accountClass === "V" && <CheckboxField name="keyAccess" label="Holds the keys" />}
    </CrudDialog>
  );
}
